import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import PropTypes from 'prop-types';
import { DRAG_NODE, DRAG_SET, NODE_MENU } from '../../redux/actions/board';
import { useDispatch, useSelector } from 'react-redux';
import LocalizedStrings from 'react-localization';
import LOCALIZATIONS from './localizations';
import './style.css';
import { MOVEMENT_SPEEDS } from '../Board/config';
import NodeTypeIcon from '../NodeTypeIcon';
import AddtionalInformation from './AdditionalInformation';
import Evidence from './Evidence';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';

const Node = props => {

    const containerRef = useRef( null ),
    classNames = [ 'coe__node' ],
    dispatch = useDispatch(),
    { zoom } = useSelector( state => state.board ),
    somethingIsDragging = useSelector( state => state.board.dragging ),
    [ dragging, setDragging ] = useState( null ),
    [ coords, setCoords ] = useState({ x: props.x, y: props.y }),
    theme = useTheme(),
    isMobile = useMediaQuery( theme.breakpoints.down('md') ),
    labels = useMemo(() => new LocalizedStrings( LOCALIZATIONS ), []),
    locale = useSelector( state => state.app.locale );

    /**
     * @name commitCoords
     * @function
     * @description Commits coordinates to the app
     */
    const commitCoords = useCallback(() => {
        dispatch({ type: DRAG_NODE, payload: { id: props.id, ...coords } });
        setDragging( null );
    }, [ dispatch, props.id, coords, setDragging ]);

    /**
     * @name edit
     * @function
     * @description Initiates editing of a node
     * @param {EventListenerObject} e 
     */
    const edit = e => {
        dispatch({ type: NODE_MENU, payload: { anchorEl: e.currentTarget, id: props.id } });
    }

    /**
     * @name handleMove
     * @function
     * @description Handles the dragging of the node
     */
    const handleMove = useCallback( ({ clientX, clientY }) => {

        const SPEED = MOVEMENT_SPEEDS[ zoom ] * -1;

        setCoords({
            x: coords.x += ( window.oldNodeX - clientX ) * SPEED,
            y: coords.y += ( window.oldNodeY - clientY ) * SPEED
        });

        window.oldNodeX = clientX;
        window.oldNodeY = clientY;

    }, [ zoom, setCoords, coords ]);

    /**
     * @name handleTouchMove
     * @function
     * @description Handles Touch gestures
     */
    const handleTouchMove = useCallback( ({ preventDefault, changedTouches }) => {
        if( changedTouches.length > 1 ) preventDefault();
        handleMove( changedTouches[ 0 ] );
    }, [ handleMove ]);

    /**
     * @name toggleDrag
     * @function
     * @description Toggles the state of the user dragging
     * @param {boolean} state 
     * @returns {function}
     */
    const toggleDrag = state => e => {
        if( !state ){
            window.oldNodeX = null;
            window.oldNodeY = null;
            if( dragging ) setDragging( false );
        } else {

            const touches = e.changedTouches;
            if( !touches ){
                window.oldNodeX = e.clientX;
                window.oldNodeY = e.clientY;
            } else {
                window.oldNodeX = touches[ 0 ].clientX;
                window.oldNodeY = touches[ 0 ].clientY;
            }
            
            if( !dragging ) setDragging( true );
        }
    }

    /**
     * @name unbindMovement
     * @function
     * @description Unbinds movement watcher
     */
    const unbindMovement = useCallback(() => {
        setDragging( false );
    }, [ setDragging ]);

    // get status
    useEffect(() => {
        if( dragging !== null ) dispatch({ type: DRAG_SET, payload: dragging });
    }, [ dragging, dispatch ]);

    // dragging options
    useEffect(() => {

        // add event listener when dragging begins
        if( dragging ){
            if( !isMobile ){
                window.addEventListener( 'mousemove', handleMove, true );
                document.addEventListener( 'mouseup', unbindMovement, true );
            } else {
                document.addEventListener( 'touchmove', handleTouchMove, { passive: true });
                document.addEventListener( 'touchend', unbindMovement, true );
            }
        } else if( dragging !== null ){
            commitCoords();
        }

        // clean up event listener
        return () => {
            if( !isMobile ){
                window.removeEventListener( 'mousemove', handleMove, true );
                document.removeEventListener( 'mouseup', unbindMovement, true );
            } else {
                document.removeEventListener( 'touchmove', handleTouchMove, { passive: true });
                document.removeEventListener( 'touchend', unbindMovement, true );
            }
        }
    }, [ commitCoords, dragging, handleMove, handleTouchMove, isMobile, unbindMovement ]);

    // update locale
    useEffect(() => {
        if( locale !== labels.getLanguage() ) labels.setLanguage( locale )
    }, [ labels, locale ]);

    if( props.disabled ) classNames.push( classNames[ 0 ] + '--disabled' );
    if( somethingIsDragging && !dragging ) classNames.push( classNames[ 0 ] + '--noTouching' );
    if( dragging ) classNames.push( classNames[ 0 ] + '--dragging' );

    return (
        <>
            <g className={ classNames.join( ' ' ) } id={ props.id } ref={ containerRef } transform={ `translate( ${ coords.x } ${ coords.y } )`}>
                {
                    props.details?.image ?
                        <image 
                            href={ props.details.image } 
                            id={ 'image-' + props.id }
                            className="coe__node__image"
                            width="600"
                            height="300"
                            x="0" 
                            y="-310" /> :
                            null
                }
                <rect 
                    className="coe__node__container"
                    onTouchStart={ !props.disabled && isMobile ? toggleDrag( true ) : null }
                    onMouseDown={ !props.disabled && !isMobile ? toggleDrag( true ) : null } 
                    rx="5"
                    x="0" 
                    y="0" />
                <NodeTypeIcon type={ props.type } x="10" y="10" />
                <text className="coe__node__name" x="300" y="43">{ props.name }</text>
                {
                    !props.disabled ?
                        <>
                            <NodeTypeIcon type="meatball" x="550" y="10" />
                            <rect onClick={ edit } className="coe__node__options" x="550" y="0" data-id={ props.id }></rect>
                        </> :
                        null
                }
                <g transform="translate( 0 80 )">
                    <text x="0" y="0" className='coe__node--noTouching'>
                        {
                            Object.keys( props.details ).map(
                                ( key, index ) => {
                                    if( key === 'image' ) return null;
                                    return (
                                        <AddtionalInformation 
                                            key={ 'additional-info-' + index }
                                            field={ key }
                                            label={ labels[ key ] } 
                                            locale={ props.locale }
                                            type={ props.type }
                                            value={ props.details[ key ] } />
                                    )
                                }
                            )
                        }
                        <tspan x="0" dy=".75rem">&nbsp;</tspan>
                        {
                            props.evidence.map(
                                ( item, index ) => (
                                    <Evidence key={ 'evidence-' + index } { ...item } />
                                )
                            )
                        }
                    </text>
                    
                </g>
            </g>
            
        </>
    )

}

Node.propTypes = {
    height: PropTypes.number,
    id: PropTypes.string,
    name: PropTypes.string,
    width: PropTypes.number,
    x: PropTypes.number,
    y: PropTypes.number
}

export default Node
